import { useCallback, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import ModalForm from "components/modal-form";
import { INVENTORY_CONDITION_NEW } from "constants/inventory-products";
import { selectCurrentClinic } from "features/auth/auth-slice";
import InventoryProductForm, {
  InventoryProductValues
} from "features/inventory/inventory-products/components/inventory-product-form/inventory-product-form.component";
import {
  useAddInventoryProductMutation,
  useLazyGetInventoryProductQuery
} from "services/inventory-products";
import { InventoryProduct } from "services/types/inventory-products.type";
import { formatBackendDateOnly } from "utils/common";
import { getProductDisplayType } from "utils/patient-products.util";

import {
  setCustomMessage,
  setOpen
} from "../../action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "../../error-snackbar-content";
import { INVENTORY_PRODUCTS_QUERY } from "../inventory-product-field.component";

type AddInventoryProductFormProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (product: InventoryProduct) => void;
  newInventoryProduct?: Partial<InventoryProductValues>;
  allowNonStock?: boolean;
};

export default function AddInventoryProductForm({
  newInventoryProduct,
  isOpen,
  onCancel,
  onSave,
  allowNonStock = false
}: AddInventoryProductFormProps) {
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [getInventoryProduct] = useLazyGetInventoryProductQuery();
  const [addInventoryProduct, { isLoading }] = useAddInventoryProductMutation();

  const initialValues = useMemo(
    () => ({
      specification: null,
      type: newInventoryProduct?.type || "",
      subtype: newInventoryProduct?.subtype || "",
      product: newInventoryProduct?.product || undefined,
      clinic: currentClinic,
      serial_number: "",
      ear: "" as "L" | "R" | "",
      quantity: "1",
      supplier_price: "",
      condition: newInventoryProduct?.condition || INVENTORY_CONDITION_NEW,
      manufacturer_warranty_expiration_date: "",
      loss_damage_warranty_expiration_date: "",
      demoable: false,
      loanable: false,
      checked_in_at: new Date().toISOString(),
      mfr_invoice_number: "",
      barcode: ""
    }),
    [currentClinic, newInventoryProduct]
  );
  const onInventoryProductSave = useCallback(
    (data: InventoryProductValues) => {
      addInventoryProduct({
        product: Number(data.product?.id),
        clinic: Number(data.clinic?.id),
        serial_number: data.serial_number,
        quantity: Number(data.quantity || 1),
        condition: data.condition ? data.condition : undefined,
        manufacturer_warranty_expiration_date:
          data.manufacturer_warranty_expiration_date
            ? formatBackendDateOnly(data.manufacturer_warranty_expiration_date)
            : undefined,
        loss_damage_warranty_expiration_date:
          data.loss_damage_warranty_expiration_date
            ? formatBackendDateOnly(data.loss_damage_warranty_expiration_date)
            : undefined,
        checked_in_at: data.checked_in_at
          ? formatBackendDateOnly(data.checked_in_at)
          : undefined,
        specification: data.specification ? data.specification : undefined,
        ear: data.ear ? data.ear : undefined,
        loanable: data.loanable,
        demoable: data.demoable,
        mfr_invoice_number: data.mfr_invoice_number
          ? data.mfr_invoice_number
          : undefined,
        barcode: data.barcode || ""
      }).then(async (response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          dispatch(setCustomMessage("Clinic stock added"));
          dispatch(setOpen(true));

          const { data: inventoryProduct } = await getInventoryProduct({
            inventoryProductId: response.data.id,
            // Same projection the picker reads — keeps the refetched record
            // shape-compatible with the picker option list it feeds back to.
            query: INVENTORY_PRODUCTS_QUERY
          });

          inventoryProduct && onSave(inventoryProduct);
        }
      });
    },
    [addInventoryProduct, getInventoryProduct, onSave, dispatch]
  );
  return (
    <ModalForm
      isOpen={isOpen}
      onCancel={onCancel}
      isSubmitting={isLoading}
      title={`Check-in new ${
        newInventoryProduct?.product?.display_name ||
        getProductDisplayType({
          type: newInventoryProduct?.type,
          subtype: newInventoryProduct?.subtype
        })
      }`}
      hideBackdrop={false}
      formName="inventory-product"
      maxWidth="lg"
    >
      <InventoryProductForm
        initialValues={initialValues}
        onSubmit={onInventoryProductSave}
        onCancel={onCancel}
        inventoryType="clinicStock"
        allowNonStock={allowNonStock}
      />
    </ModalForm>
  );
}
